import { TextsToSpeechesDocument } from "@/generated/graphql";
import { AUDIO_PITCH_LIMIT, AUDIO_SPEED_LIMIT } from "@/lib/constants/slides";
import { useSpellChecker } from "@/lib/hooks/shared/useSpellChecker";
import { toastAiCredits } from "@/lib/utils/aiCredits";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateSlide } from "@/store/slideEditorSlice";
import { MediaUsageContextSchema, type Passage, type ReadingSlideData } from "@ai-teach/types";
import { Button, Modal } from "@ai-teach/ui-web";
import { AZURE_VOICES } from "@ai-teach/utils";
import { useMutation } from "@apollo/client/react";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FaChevronDown, FaPlay, FaStop } from "react-icons/fa";
import { HiSparkles } from "react-icons/hi";
import { MdSpellcheck } from "react-icons/md";
import { PiSpeakerHighFill } from "react-icons/pi";

const SPEED_LIMIT = AUDIO_SPEED_LIMIT;
const PITCH_LIMIT = AUDIO_PITCH_LIMIT;

interface ReadingFluencyHeaderProps {
  voiceName: string;
  setVoiceName: Dispatch<SetStateAction<string>>;
  onMisspelledWords: (misspelledByPassage: Record<string, string[]>) => void;
}

export function ReadingFluencyHeader({
  voiceName,
  setVoiceName,
  onMisspelledWords,
}: ReadingFluencyHeaderProps) {
  const dispatch = useAppDispatch();
  const { slide } = useAppSelector((state) => state.slideEditor.present);
  const { checkAllPassages } = useSpellChecker();

  const [isPlayingAll, setIsPlayingAll] = useState(false);
  const currentHandlerRef = useRef<(() => void) | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Generate all modal state
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [genVoice, setGenVoice] = useState(voiceName);
  const [genSpeed, setGenSpeed] = useState<number>(SPEED_LIMIT.default);
  const [genPitch, setGenPitch] = useState<number>(PITCH_LIMIT.default);
  const [genVoiceDropdownOpen, setGenVoiceDropdownOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const genVoiceDropdownRef = useRef<HTMLDivElement>(null);

  if (!slide) return null;

  const readingSlideData = slide.slideData as ReadingSlideData;

  const hasAnyText = readingSlideData.passages.some((p) => p.text?.trim());
  const hasAnyAudio = readingSlideData.passages.some((p) => p.audio?.src);

  const handleIsTextOnly = (value: string) => {
    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: {
          ...readingSlideData,
          isTextOnly: value === "textOnly",
        },
      })
    );
  };

  const handleCheckAllSpelling = async () => {
    toast.loading("Checking all passages...", { id: "spellCheckAllToast" });

    try {
      const { updatedPassages, passedCount, failedCount, misspelledByPassage } =
        await checkAllPassages(readingSlideData.passages);

      onMisspelledWords(misspelledByPassage);

      dispatch(
        updateSlide({
          slideId: slide.id,
          slideData: {
            ...readingSlideData,
            passages: updatedPassages,
          },
        })
      );

      if (failedCount === 0) {
        toast.success(`All ${passedCount} passages passed spell check!`, {
          id: "spellCheckAllToast",
        });
      } else {
        toast.error(`${passedCount} passed, ${failedCount} need attention`, {
          id: "spellCheckAllToast",
          duration: 5000,
        });
      }
    } catch (error) {
      if (toastAiCredits(error, "spellCheckAllToast")) return;
      toast.error("Failed to check spelling!", { id: "spellCheckAllToast" });
    }
  };

  const [textsToSpeechesMutation] = useMutation(TextsToSpeechesDocument);

  // Video passages read with their own soundtrack, so a batch generate leaves them alone.
  const wantsAudio = (passage: Passage) =>
    !!readingSlideData.isTextOnly || passage.mediaType !== "video";

  const handleGenerateAllAudio = async (voice: string, speed: number, pitch: number) => {
    const validPassages = readingSlideData.passages.filter((p) => p.text && wantsAudio(p));

    if (validPassages.length === 0) return;

    setIsGenerating(true);
    toast.loading("Generating Audio", { id: "generatingAudioToast" });

    try {
      const result = await textsToSpeechesMutation({
        variables: {
          texts: validPassages.map((p) => p.text ?? ""),
          voice,
          slideId: slide.id,
          usageContext: MediaUsageContextSchema.Values.slideEditor,
          speed,
          pitch,
        },
      });

      const audioResults = result.data?.textsToSpeeches ?? [];

      const audioMap = new Map(
        validPassages.map((passage, index) => [passage.text, audioResults[index]])
      );

      const updatedPassages = readingSlideData.passages.map((p) => {
        if (!wantsAudio(p)) return p;

        const audioData = audioMap.get(p.text);

        if (!audioData) return p;

        return {
          ...p,
          audio: {
            id: audioData.id ?? null,
            src: audioData.src ?? null,
            filename: audioData.filename ?? null,
            voice,
          },
        };
      });

      dispatch(
        updateSlide({
          slideId: slide.id,
          slideData: {
            ...readingSlideData,
            passages: updatedPassages,
          },
        })
      );

      setVoiceName(voice);
      toast.success("Audio generated", { id: "generatingAudioToast" });
      setIsGenerateModalOpen(false);
    } catch (error) {
      if (!toastAiCredits(error, "generatingAudioToast")) {
        toast.error("Failed to generate audio!", { id: "generatingAudioToast" });
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleOpenGenerateModal = () => {
    setGenVoice(voiceName);
    setGenSpeed(SPEED_LIMIT.default);
    setGenPitch(PITCH_LIMIT.default);
    setIsGenerateModalOpen(true);
  };

  const playAudioAtIndex = (index: number, passagesWithAudio: Passage[]) => {
    if (index >= passagesWithAudio.length) {
      setIsPlayingAll(false);
      toast.success("Finished playing all audio", { duration: 2000 });
      return;
    }
    cleanupCurrentAudio();
    const currentAudioSrc = passagesWithAudio[index]?.audio?.src;
    if (!currentAudioSrc) return;
    const audio = new Audio(currentAudioSrc);
    audioRef.current = audio;

    const handlerEnded = () => {
      playAudioAtIndex(index + 1, passagesWithAudio);
    };

    currentHandlerRef.current = handlerEnded;
    audio.addEventListener("ended", handlerEnded);
    audio.play();
  };

  const cleanupCurrentAudio = () => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.pause();
    if (currentHandlerRef.current) {
      audio.removeEventListener("ended", currentHandlerRef.current);
    }
  };

  const handlePlayAllAudio = () => {
    const passagesWithAudio = readingSlideData.passages.filter((p) => p.audio?.src);

    if (passagesWithAudio.length === 0) {
      toast.error("No audio files available to play", { duration: 2000 });
      return;
    }

    if (isPlayingAll) {
      cleanupCurrentAudio();
      setIsPlayingAll(false);
      return;
    }

    setIsPlayingAll(true);
    playAudioAtIndex(0, passagesWithAudio);
  };

  const selectedVoice = AZURE_VOICES.find((v) => v.value === voiceName) ?? AZURE_VOICES[0];
  const selectedGenVoice = AZURE_VOICES.find((v) => v.value === genVoice) ?? AZURE_VOICES[0];
  const [voiceDropdownOpen, setVoiceDropdownOpen] = useState(false);
  const voiceDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!voiceDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (voiceDropdownRef.current && !voiceDropdownRef.current.contains(e.target as Node)) {
        setVoiceDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [voiceDropdownOpen]);

  useEffect(() => {
    if (!genVoiceDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (genVoiceDropdownRef.current && !genVoiceDropdownRef.current.contains(e.target as Node)) {
        setGenVoiceDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [genVoiceDropdownOpen]);

  return (
    <>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2">
        {/* Passage layout mode — changes how every passage renders */}
        <div className="relative shrink-0">
          <select
            name="isTextOnly"
            id="isTextOnly"
            onChange={(e) => handleIsTextOnly(e.target.value)}
            value={readingSlideData.isTextOnly ? "textOnly" : "textImage"}
            className="h-7 pl-2 pr-6 appearance-none rounded-md border border-slate-200 bg-white hover:bg-primary-50 hover:border-primary text-[11px] font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-400/40 focus:border-primary-400 transition-colors"
          >
            <option value="textOnly">Text Only</option>
            <option value="textImage">Text + Image</option>
          </select>
          <FaChevronDown
            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
            style={{ fontSize: "8px" }}
          />
        </div>

        {/* Batch actions — voice sits with them since it's the voice they generate with */}
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          {/* Custom voice selector */}
          <div ref={voiceDropdownRef} className="relative w-[190px] shrink-0">
            <button
              type="button"
              onClick={() => setVoiceDropdownOpen((o) => !o)}
              className="w-full h-7 pl-2.5 pr-2 flex items-center gap-1.5 rounded-md border border-slate-200 bg-white hover:bg-primary-50 hover:border-primary-300 transition-all duration-200 shadow-sm"
            >
              <span className="flex-1 min-w-0 text-left flex items-center gap-2">
                <span className="block text-[11px] font-semibold text-slate-800 truncate leading-none">
                  {selectedVoice.label}
                </span>
                <span className="block text-[9px] text-slate-400 truncate leading-none mt-0.5">
                  {selectedVoice.detail}
                </span>
              </span>
              <FaChevronDown
                className={`text-slate-400 shrink-0 transition-transform duration-200 ${voiceDropdownOpen ? "rotate-180" : ""}`}
                style={{ fontSize: "8px" }}
              />
            </button>

            {voiceDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
                {AZURE_VOICES.map((voice) => (
                  <button
                    key={voice.value}
                    type="button"
                    onClick={() => {
                      setVoiceName(voice.value);
                      setVoiceDropdownOpen(false);
                    }}
                    className={`w-full px-3 py-1.5 flex items-center gap-2 text-left transition-colors ${
                      voice.value === voiceName
                        ? "bg-primary-50 text-primary-700"
                        : "hover:bg-slate-50 text-slate-700"
                    }`}
                  >
                    <span className="flex-1 min-w-0">
                      <span className="block text-[11px] font-semibold truncate">
                        {voice.label}
                      </span>
                      <span className="block text-[9px] text-slate-400 truncate">
                        {voice.detail}
                      </span>
                    </span>
                    {voice.value === voiceName && (
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-500 shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Button
            variant="primary"
            size="sm"
            className="gap-1 text-xs"
            onClick={handleCheckAllSpelling}
            disabled={!hasAnyText}
          >
            <MdSpellcheck className="text-sm" />
            <span>Spell Check</span>
          </Button>

          <Button
            variant="ghost"
            size="sm"
            className="gap-1 text-xs"
            onClick={handlePlayAllAudio}
            disabled={!hasAnyAudio && !isPlayingAll}
          >
            {isPlayingAll ? <FaStop className="text-xs" /> : <FaPlay className="text-xs" />}
            <span>{isPlayingAll ? "Stop" : "Play All"}</span>
          </Button>

          <Button
            variant="secondary"
            size="sm"
            className="gap-1 text-xs"
            onClick={handleOpenGenerateModal}
            disabled={!hasAnyText}
          >
            <PiSpeakerHighFill className="text-sm" />
            <span>Generate All</span>
          </Button>
        </div>
      </div>

      {/* Generate All Audio modal */}
      <Modal
        title="Generate All Audio"
        isOpen={isGenerateModalOpen}
        onClose={() => setIsGenerateModalOpen(false)}
        size="sm"
      >
        <div className="flex flex-col gap-4 pt-1">
          {/* Voice */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Voice
            </label>
            <div ref={genVoiceDropdownRef} className="relative">
              <button
                type="button"
                onClick={() => setGenVoiceDropdownOpen((o) => !o)}
                className="w-full h-8 pl-2.5 pr-2 flex items-center gap-1.5 rounded-md border border-slate-200 bg-white hover:bg-primary-50 hover:border-primary-300 transition-all duration-200 shadow-sm"
              >
                <span className="flex-1 min-w-0 text-left flex items-center gap-2">
                  <span className="text-[11px] font-semibold text-slate-800 truncate leading-none">
                    {selectedGenVoice.label}
                  </span>
                  <span className="text-[9px] text-slate-400 truncate leading-none">
                    {selectedGenVoice.detail}
                  </span>
                </span>
                <FaChevronDown
                  className={`text-slate-400 shrink-0 transition-transform duration-200 ${genVoiceDropdownOpen ? "rotate-180" : ""}`}
                  style={{ fontSize: "8px" }}
                />
              </button>

              {genVoiceDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden max-h-48 overflow-y-auto">
                  {AZURE_VOICES.map((v) => (
                    <button
                      key={v.value}
                      type="button"
                      onClick={() => {
                        setGenVoice(v.value);
                        setGenVoiceDropdownOpen(false);
                      }}
                      className={`w-full px-3 py-1.5 flex items-center gap-2 text-left transition-colors ${
                        v.value === genVoice
                          ? "bg-primary-50 text-primary-700"
                          : "hover:bg-slate-50 text-slate-700"
                      }`}
                    >
                      <span className="flex-1 min-w-0">
                        <span className="block text-[11px] font-semibold truncate">{v.label}</span>
                        <span className="block text-[9px] text-slate-400 truncate">{v.detail}</span>
                      </span>
                      {v.value === genVoice && (
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-500 shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Speed */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Speed
              </label>
              <span className="text-xs font-semibold text-slate-600 tabular-nums">
                {(genSpeed / 100).toFixed(2)}x
              </span>
            </div>
            <input
              type="range"
              min={SPEED_LIMIT.min}
              max={SPEED_LIMIT.max}
              value={genSpeed}
              onChange={(e) => setGenSpeed(Number(e.target.value))}
              className="w-full accent-primary-500 cursor-pointer"
            />
            <div className="flex justify-between text-[10px] text-slate-400">
              <span>{SPEED_LIMIT.min / 100}x</span>
              <span>{SPEED_LIMIT.max / 100}x</span>
            </div>
          </div>

          {/* Pitch */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Pitch
              </label>
              <span className="text-xs font-semibold text-slate-600 tabular-nums">
                {genPitch > 0 ? `+${genPitch}` : genPitch}
              </span>
            </div>
            <input
              type="range"
              min={PITCH_LIMIT.min}
              max={PITCH_LIMIT.max}
              value={genPitch}
              onChange={(e) => setGenPitch(Number(e.target.value))}
              className="w-full accent-primary-500 cursor-pointer"
            />
            <div className="flex justify-between text-[10px] text-slate-400">
              <span>{PITCH_LIMIT.min}</span>
              <span>{PITCH_LIMIT.default}</span>
              <span>+{PITCH_LIMIT.max}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <Button
              variant="ghost"
              size="sm"
              className="flex-1"
              onClick={() => setIsGenerateModalOpen(false)}
              disabled={isGenerating}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="flex-1 gap-1.5"
              onClick={() => handleGenerateAllAudio(genVoice, genSpeed / 100, genPitch)}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  <HiSparkles />
                  Generate All
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
